'use client';

import React, { useState } from 'react';
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import Header from "@/components/layout/Header";
import Footer from "@/components/sections/Footer";
import { Seo } from "@/components/Seo";
import { softwareApplicationSchema, AeoAnswer } from "@/features/seo";
import CalculatorForm, { CalculationResult } from '@/features/calculator/components/CalculatorForm';
import SavingsVisualizer from '@/features/calculator/components/SavingsVisualizer';
import { Calculator, Zap, ShieldCheck, Globe, HelpCircle } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import { useTranslations } from '@/lib/i18n-client';

function CalculatorContent({ onResult }: { onResult: (res: CalculationResult | null) => void }) {
  const searchParams = useSearchParams();
  // Fixed: searchParams.get() handles null check internally, but searchParams itself could be null in some environments
  const initialDestination = searchParams?.get('destination') || undefined;

  return <CalculatorForm onCalculate={onResult} initialDestination={initialDestination} />;
}

export default function CalculatorUI() {
  const { t } = useTranslations();
  const [result, setResult] = useState<CalculationResult | null>(null);

  return (
    <div className="brand-utility-page min-h-screen bg-background pattern-overlay">
      <Seo
        route="/calculator/"
        jsonLd={softwareApplicationSchema}
        breadcrumbs={[
          { name: t('breadcrumbs.home', 'Home'), url: "/" },
          { name: t('breadcrumbs.calculator', 'Savings Calculator'), url: "/calculator/" }
        ]}
      />
      <Header />

      <main className="pt-24 sm:pt-36 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-16 relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-bold uppercase tracking-widest mb-6 animate-fade-in">
              <Calculator className="w-4 h-4" />
              <span>{t('calculatorUI.badge', 'Free Engineering Tool')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mb-8 leading-[1.05]">
              {t('calculatorUI.heroTitle', 'Stop Paying')} <span className="text-primary italic">{t('calculatorUI.heroRoaming', 'Roaming')}</span> {t('calculatorUI.heroFees', 'Fees')}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {t('calculatorUI.heroDesc', "Use our interactive calculator to see exactly how much you'll save by switching to an eSIMSupra travel plan before your next trip.")}
            </p>
          </div>

          <div className="max-w-4xl mx-auto mb-12">
            <AeoAnswer question={t('calculatorUI.aeoQuestion', 'How much can you save by using a travel eSIM instead of carrier roaming?')}>
              <p>
                {t('calculatorUI.aeoBody1', 'Typical savings range from ')}<strong>{t('calculatorUI.aeoBold1', '60–80%')}</strong>{t('calculatorUI.aeoBody2', ' on week-long trips and ')}<strong>{t('calculatorUI.aeoBold2', ' 80–90%')}</strong>{t('calculatorUI.aeoBody3', ' on month-long trips compared to carrier roaming day passes. A US carrier day pass averages ')}<strong>{t('calculatorUI.aeoBold3', '$10–$12/day')}</strong>{t('calculatorUI.aeoBody4', ' per line; a 10-day Europe eSIM plan with the same data headroom costs around ')}<strong>{t('calculatorUI.aeoBold4', '$20–$25')}</strong>{t('calculatorUI.aeoBody5', ' total. The calculator below checks your specific destination, trip length, and data appetite against current eSIMSupra plans to produce a dollar amount you can plan against.')}
              </p>
            </AeoAnswer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left: Form */}
            <div className="space-y-8">
              <Suspense fallback={<div className="h-[400px] glass animate-pulse rounded-3xl" />}>
                <CalculatorContent onResult={setResult} />
              </Suspense>
              
              {/* Trust & Features */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-6 glass rounded-3xl border border-border/50 flex gap-4">
                  <ShieldCheck className="w-6 h-6 text-primary shrink-0" />
                  <div>
                    <h3 className="font-bold text-sm">{t('calculatorUI.benchmarkTitle', 'Accurate Benchmarks')}</h3>
                    <p className="text-xs text-muted-foreground">{t('calculatorUI.benchmarkDesc', 'Updated May 2026 data for US/UK/EU telcos.')}</p>
                  </div>
                </div>
                <div className="p-6 glass rounded-3xl border border-border/50 flex gap-4">
                  <Globe className="w-6 h-6 text-primary shrink-0" />
                  <div>
                    <h3 className="font-bold text-sm">{t('calculatorUI.destinationsTitle', '180+ Destinations')}</h3>
                    <p className="text-xs text-muted-foreground">{t('calculatorUI.destinationsDesc', 'Local rates for every corner of the globe.')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Visualization or Placeholder */}
            <div className="min-h-[600px] flex flex-col items-center justify-center">
              {result ? (
                <SavingsVisualizer result={result} />
              ) : (
                <div className="text-center space-y-8 animate-pulse p-12 glass rounded-[3rem] border border-dashed border-border/50 w-full">
                  <div className="w-24 h-24 bg-primary/5 rounded-full flex items-center justify-center mx-auto">
                    <Zap className="w-12 h-12 text-primary/30" />
                  </div>
                  <div className="space-y-4">
                    <h2 className="text-2xl font-bold opacity-30">{t('calculatorUI.placeholderTitle', 'Ready to calculate?')}</h2>
                    <p className="text-muted-foreground max-w-xs mx-auto">
                      {t('calculatorUI.placeholderDesc', 'Fill in your destination and trip details on the left to see your personalized savings report.')}
                    </p>
                  </div>
                  <div className="pt-8 grid grid-cols-2 gap-4 opacity-10">
                    <div className="h-20 bg-muted rounded-2xl" />
                    <div className="h-20 bg-muted rounded-2xl" />
                  </div>
                </div>
              )}
            </div>
          </div>


          {/* Simple FAQ / Utility Section */}
          <section className="mt-32 pt-20 border-t border-border/30">
            <div className="max-w-3xl mx-auto space-y-12">
              <div className="text-center">
                <h2 className="text-3xl font-black mb-4">{t('calculatorUI.howItWorks', 'How it works')}</h2>
                <p className="text-muted-foreground">{t('calculatorUI.howItWorksDesc', "Transparency is our core value. Here's how we calculate your travel data savings.")}</p>
              </div>
              
              <div className="grid gap-6">
                {[
                  {
                    q: t('calculatorUI.faq1Q', 'Where do you get the carrier roaming costs and benchmarks?'),
                    a: t('calculatorUI.faq1A', 'Our calculator utilizes a regularly updated database of international roaming benchmarks for major global carriers, including US-based telcos like AT&T, Verizon, and T-Mobile (based on their International Day Pass rates), as well as UK carriers such as EE, Vodafone, and O2, and major European providers. We specifically track the lowest entry-level daily roaming fees that offer high-speed data access. By comparing these standard fees with our wholesale-priced local eSIM rates, we can provide a highly accurate projection of the potential savings you can expect during your specific trip duration and destination.')
                  },
                  {
                    q: t('calculatorUI.faq2Q', "How accurate is the 'Best Plan' matching engine?"),
                    a: t('calculatorUI.faq2A', "The matching engine scans over 5,000 active eSIM packages in our real-time database to identify the optimal configuration for your trip. It analyzes your selected 'usage profile'—whether you're a light user primarily using messaging or a heavy user streaming video and using navigation—and maps that against the most cost-effective plan that covers your entire trip duration. We prioritize plans that offer the best balance of data volume and validity duration, ensuring you don't overpay for unused data while never running out of connectivity when you need it most.")
                  },
                  {
                    q: t('calculatorUI.faq3Q', 'Is this calculator suitable for business travelers and digital nomads?'),
                    a: t('calculatorUI.faq3A', 'Absolutely. Business travelers and digital nomads are often the most affected by exorbitant roaming fees because they require consistent, high-speed connectivity for communication, video conferencing, and cloud-based applications. A typical heavy usage profile (2GB+ per day) can cost over $100 per week with traditional US carriers using daily passes. Our calculator demonstrates why enterprise-grade connectivity through regional or country-specific eSIMs is often ten times more economical, providing the same native 5G performance without the corporate roaming price tag.')
                  }
                ].map((item, i) => (
                  <div key={i} className="p-8 glass rounded-3xl border border-border/30">
                    <div className="flex gap-4">
                      <HelpCircle className="w-6 h-6 text-primary shrink-0" />
                      <div>
                        <h3 className="font-bold text-lg mb-2">{item.q}</h3>
                        <p className="text-muted-foreground leading-relaxed">{item.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Internal links — popular destinations to calculate. Drives users from
                  calculator (info intent) to destination pages (commercial intent). */}
              <div className="pt-8">
                <h3 className="text-xl font-bold mb-4 text-center">{t('calculatorUI.popularDestinations', 'Popular destinations to calculate')}</h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {[
                    { code: 'jp', name: 'Japan' },
                    { code: 'us', name: 'United States' },
                    { code: 'gb', name: 'United Kingdom' },
                    { code: 'th', name: 'Thailand' },
                    { code: 'fr', name: 'France' },
                    { code: 'de', name: 'Germany' },
                    { code: 'es', name: 'Spain' },
                    { code: 'it', name: 'Italy' },
                  ].map(d => (
                    <Link
                      key={d.code}
                      href={`/destinations/${d.code}/`}
                      className="px-4 py-2 rounded-full border border-border/60 bg-muted/20 hover:bg-primary/10 hover:border-primary/30 transition-colors text-sm font-medium text-foreground"
                    >
                      {t('calculatorUI.esimFor', 'eSIM for {name}').replace('{name}', d.name)}
                    </Link>
                  ))}
                </div>
                <p className="text-center text-sm text-muted-foreground mt-6">
                  {t('calculatorUI.comparingProviders', 'Comparing providers?')}{" "}
                  <Link href="/comparisons/supraesim-vs-airalo/" className="text-primary hover:underline font-medium">
                    {t('calculatorUI.compareAiralo', 'See how eSIMSupra compares to Airalo')}
                  </Link>
                  {" "}{t('calculatorUI.or', 'or')}{" "}
                  <Link href="/comparisons/" className="text-primary hover:underline font-medium">
                    {t('calculatorUI.browseAll', 'browse all comparisons')}
                  </Link>
                  .
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
